import React, { useCallback, useEffect, useMemo, useState } from "react";
import ArchiveIcon from "@mui/icons-material/Archive";
import DashboardIcon from "@mui/icons-material/Dashboard";
import EditIcon from "@mui/icons-material/Edit";
import LocalLibraryIcon from "@mui/icons-material/LocalLibrary";
import SettingsIcon from "@mui/icons-material/Settings";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import TollIcon from "@mui/icons-material/Toll";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { trackEvent } from "../../services/GoogleAnalytics";
import HomeService from "../../services/HomeService";
import Logger from "../../services/Logger";
import { hasWebPremiumSupportAccess } from "../../services/PayPlanUtil";
import SubscriptionService from "../../services/SubscriptionService";
import UserService from "../../services/UserService";
import WordWheelService from "../puzzles/wordwheel/WordWheelService";
import { ACCENT, SURFACE } from "../../dark-theme/palette";
import { usePagesTranslation } from "../locales";
import { usePageTitle } from "../puzzles/common/hooks/usePageTitle";
import { DEFAULT_SELECTED_PLAN, SUBSCRIPTION_PLANS } from "../subscription/PanelPlan";
import DashboardContinueRow from "./DashboardContinueRow";
import DashboardDailyPuzzlesWidget from "./DashboardDailyPuzzlesWidget";
import DashboardWordWheelWidget from "./DashboardWordWheelWidget";
import WordWheelWallet from "../puzzles/wordwheel/WordWheelWallet";

const WEEKDAY_LABELS = ["weekday-sun", "weekday-mon", "weekday-tue", "weekday-wed", "weekday-thu", "weekday-fri", "weekday-sat"];

function greetingName(cloudUser) {
    const first = String(cloudUser?.firstName || "").trim();
    return first || "";
}

export default function UserDashboardPage() {
    const { t } = usePagesTranslation();
    usePageTitle(t("dashboard.meta-title"));
    const [loading, setLoading] = useState(true);
    const [cloudUser, setCloudUser] = useState(null);
    const [playStats, setPlayStats] = useState({ countPlay: 0, countFinished: 0 });
    const [wordWheelLevel, setWordWheelLevel] = useState(null);
    const [nameOpen, setNameOpen] = useState(false);
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [savingName, setSavingName] = useState(false);
    const [purchasing, setPurchasing] = useState(false);
    const [purchaseError, setPurchaseError] = useState("");

    useEffect(() => {
        trackEvent("entered", { name: "UserDashboardPage" });
    }, []);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const userInfo = await UserService.getUserInfo();
            const cu = userInfo?.cloudUser || null;
            setCloudUser(cu);
            setFirstName(cu?.firstName || "");
            setLastName(cu?.lastName || "");
            try {
                const stats = await HomeService.getPlayStats();
                setPlayStats({
                    countPlay: stats?.countPlay ?? 0,
                    countFinished: stats?.countFinished ?? 0,
                });
            } catch (e) {
                Logger.error("Dashboard play stats:", e);
            }
            try {
                const ww = await WordWheelService.fetchNext();
                const n = Number(ww?.mainJourneyLevel ?? ww?.puzzleLevel);
                setWordWheelLevel(Number.isFinite(n) && n >= 1 ? n : null);
            } catch (e) {
                Logger.warn("Dashboard Word Wheel level load failed:", e);
                setWordWheelLevel(null);
            }
        } catch (e) {
            Logger.error("Dashboard user load:", e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const first = greetingName(cloudUser);
    const streak = Number(cloudUser?.stats?.currentStreak) || 0;
    const inProgress = Math.max(0, (playStats.countPlay || 0) - (playStats.countFinished || 0));
    const supportOk = hasWebPremiumSupportAccess(cloudUser);
    const planLabel = supportOk ? t("dashboard.plan-premium") : t("dashboard.plan-free");

    const weekdayDots = useMemo(() => {
        const n = Math.max(0, Math.min(7, streak));
        const today = new Date();
        const dots = [];
        for (let daysAgo = 6; daysAgo >= 0; daysAgo -= 1) {
            const d = new Date(today);
            d.setDate(today.getDate() - daysAgo);
            dots.push({
                key: daysAgo,
                filled: daysAgo < n,
                label: t(`dashboard.${WEEKDAY_LABELS[d.getDay()]}`),
            });
        }
        return dots;
    }, [streak, t]);

    const handlePurchasePremium = async () => {
        if (purchasing) return;
        setPurchasing(true);
        setPurchaseError("");
        try {
            const result = await SubscriptionService.handleSubscribe(
                DEFAULT_SELECTED_PLAN,
                SUBSCRIPTION_PLANS,
            );
            if (result.requiresLogin) {
                window.location.href = "/login";
                return;
            }
            if (!result.success) {
                setPurchaseError(result.error || t("dashboard.buy-premium-error"));
            }
        } catch (e) {
            Logger.error("Dashboard purchase:", e);
            setPurchaseError(e?.message || t("dashboard.buy-premium-error"));
        } finally {
            setPurchasing(false);
        }
    };

    const saveName = async () => {
        if (!cloudUser?.id) return;
        setSavingName(true);
        try {
            await UserService.updateUsername(cloudUser.id, firstName.trim(), lastName.trim());
            setCloudUser((prev) => ({
                ...prev,
                firstName: firstName.trim(),
                lastName: lastName.trim(),
            }));
            setNameOpen(false);
            window.dispatchEvent(new CustomEvent("puzzleAuthChanged"));
        } catch (e) {
            Logger.error("Dashboard save name:", e);
        } finally {
            setSavingName(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 360 }}>
                <CircularProgress />
            </Box>
        );
    }

    const shortcuts = [
        { key: "library", to: "/account/library", icon: <ArchiveIcon />, label: t("dashboard.shortcut-library") },
        { key: "vocabulary", to: "/account/vocabulary", icon: <LocalLibraryIcon />, label: t("dashboard.shortcut-vocabulary") },
        { key: "settings", to: "/account/settings", icon: <SettingsIcon />, label: t("dashboard.shortcut-settings") },
        { key: "subscription", to: "/subscription/plans", icon: <TollIcon />, label: t("dashboard.shortcut-subscription") },
    ];

    return (
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, maxWidth: 1200, mx: "auto", width: "100%", boxSizing: "border-box" }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap sx={{ mb: 1, rowGap: 1, columnGap: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                <DashboardIcon color="primary" />
                <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: "-0.02em" }}>
                    {first
                        ? t("dashboard.hello-name", { name: first })
                        : t("dashboard.hello-puzzle-lover")}
                </Typography>
                <Tooltip title={t("dashboard.edit-name")}>
                    <IconButton aria-label={t("dashboard.edit-name")} onClick={() => setNameOpen(true)} sx={{ minWidth: 44, minHeight: 44 }}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                {supportOk ? (
                    <Chip
                        size="small"
                        component={RouterLink}
                        to="/account/profile"
                        clickable
                        label={planLabel}
                        color="success"
                        sx={{ textDecoration: "none" }}
                    />
                ) : (
                    <Chip
                        size="small"
                        component="button"
                        type="button"
                        clickable
                        label={planLabel}
                        color="default"
                        onClick={handlePurchasePremium}
                        disabled={purchasing}
                    />
                )}
                {!supportOk ? (
                    <Tooltip title={t("dashboard.buy-premium")}>
                        <span>
                            <IconButton
                                aria-label={t("dashboard.buy-premium")}
                                onClick={handlePurchasePremium}
                                disabled={purchasing}
                                sx={{ minWidth: 44, minHeight: 44, color: "primary.main" }}
                            >
                                {purchasing ? (
                                    <CircularProgress size={18} color="inherit" />
                                ) : (
                                    <ShoppingCartIcon fontSize="small" />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                ) : null}
                </Stack>
                <Box sx={{ ml: "auto" }}>
                    <WordWheelWallet tone="dashboard" hideGift showLabels />
                </Box>
            </Stack>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: SURFACE.card, borderColor: SURFACE.border, height: "100%" }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <WhatshotIcon sx={{ color: "#ffb74d" }} />
                            <Typography variant="subtitle2" color="text.secondary">
                                {t("dashboard.streak")}
                            </Typography>
                        </Stack>
                        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                            {t("dashboard.streak-days", { count: streak })}
                        </Typography>
                        <Stack direction="row" spacing={0.75} justifyContent="space-between">
                            {weekdayDots.map((dot) => (
                                <Box key={dot.key} sx={{ textAlign: "center", flex: 1 }}>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            borderRadius: "50%",
                                            mx: "auto",
                                            mb: 0.5,
                                            bgcolor: dot.filled ? ACCENT.main : SURFACE.sunken,
                                            border: "1px solid",
                                            borderColor: dot.filled ? ACCENT.main : SURFACE.border,
                                        }}
                                    />
                                    <Typography variant="caption" color="text.secondary">
                                        {dot.label}
                                    </Typography>
                                </Box>
                            ))}
                        </Stack>
                    </Paper>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: SURFACE.card, borderColor: SURFACE.border, height: "100%" }}>
                        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                            {t("dashboard.progress")}
                        </Typography>
                        <Stack direction="row" spacing={2} justifyContent="space-between">
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>{playStats.countPlay || 0}</Typography>
                                <Typography variant="caption" color="text.secondary">{t("dashboard.plays-started")}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>{playStats.countFinished || 0}</Typography>
                                <Typography variant="caption" color="text.secondary">{t("dashboard.plays-completed")}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>{inProgress}</Typography>
                                <Typography variant="caption" color="text.secondary">{t("dashboard.plays-in-progress")}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                    {wordWheelLevel != null ? t("wordwheel.journey-level", { level: wordWheelLevel }) : "—"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">{t("dashboard.word-wheel-level")}</Typography>
                            </Box>
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>

            <Typography variant="h6" sx={{ fontWeight: 700, mt: 10, mb: 1.5 }}>
                {t("dashboard.daily")}
            </Typography>
            <Grid container spacing={2} alignItems="stretch">
                <Grid size={{ xs: 12, md: 4 }} sx={{ minWidth: 0, display: "flex" }}>
                    <DashboardWordWheelWidget />
                </Grid>
                <DashboardDailyPuzzlesWidget />
            </Grid>

            <Box sx={{ mt: 10 }}>
                <DashboardContinueRow />
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 700, mt: 10, mb: 1.5 }}>
                {t("dashboard.shortcuts")}
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 4 }}>
                {shortcuts.map((item) => (
                    <Grid key={item.key} size={{ xs: 6, sm: 4, md: 3 }}>
                        <Paper
                            component={RouterLink}
                            to={item.to}
                            variant="outlined"
                            sx={{
                                p: 1.5,
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                minHeight: 56,
                                textDecoration: "none",
                                color: "inherit",
                                bgcolor: SURFACE.card,
                                borderColor: SURFACE.border,
                                "&:hover": { borderColor: ACCENT.main },
                            }}
                        >
                            {item.icon}
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                                    {item.label}
                                </Typography>
                                {item.disabledHint ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }} noWrap>
                                        {item.disabledHint}
                                    </Typography>
                                ) : null}
                            </Box>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            <Snackbar
                open={Boolean(purchaseError)}
                autoHideDuration={6000}
                onClose={() => setPurchaseError("")}
                message={purchaseError}
            />

            <Dialog open={nameOpen} onClose={() => setNameOpen(false)} fullWidth maxWidth="xs">
                <DialogTitle>{t("dashboard.edit-name")}</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            label={t("dashboard.first-name")}
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            autoFocus
                            fullWidth
                        />
                        <TextField
                            label={t("dashboard.last-name")}
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setNameOpen(false)} sx={{ textTransform: "none" }}>
                        {t("btn.cancel")}
                    </Button>
                    <Button onClick={saveName} disabled={savingName} variant="contained" sx={{ textTransform: "none" }}>
                        {savingName ? <CircularProgress size={18} /> : t("dashboard.save-name")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
